import React, { useState } from 'react';
import { FiMail, FiInstagram, FiMessageSquare, FiSend, FiCheck, FiAlertCircle } from 'react-icons/fi';
import { RiWhatsappFill } from 'react-icons/ri';

const ContactUs: React.FC = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<{
    type: 'success' | 'error' | null;
    message: string;
  }>({ type: null, message: '' });
  
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus({ type: null, message: '' });
    
    try {
      const response = await fetch('/api/contact-form', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      const result = await response.json();

      if (response.ok && result.success) {
        setSubmitStatus({
          type: 'success',
          message: result.message || 'Your message has been sent successfully!'
        });
        setFormData({ name: '', email: '', subject: '', message: '' });
      } else if (response.status === 429) {
        // Rate limiting error
        const retryAfter = result.retryAfter || 60;
        const minutes = Math.ceil(retryAfter / 60);
        setSubmitStatus({
          type: 'error',
          message: `${result.error || 'Too many requests.'} Please wait ${minutes} minute${minutes > 1 ? 's' : ''} before trying again.`
        });
      } else {
        setSubmitStatus({
          type: 'error',
          message: result.error || 'Failed to send your message. Please try again.'
        });
      }
    } catch (error) {
      console.error('Error submitting contact form:', error);
      setSubmitStatus({
        type: 'error',
        message: 'An error occurred. Please try again later.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };
  
  return (
    <div id="contact" className="container mx-auto px-4 py-6 max-w-3xl">
      <div className="flex flex-col space-y-6">
        {/* Newsletter Section */}
        <div className="relative overflow-hidden bg-[#10081a] rounded-lg p-6 w-full">
          <h2 className="text-2xl font-bold text-white text-center mb-3">Stay Updated</h2>
          <div className="w-20 h-1 bg-accent mx-auto mb-4"></div>
          
          <p className="text-center text-gray-300 mb-6">
            Subscribe to our newsletter for exclusive drops, special offers, and early access to 
            upcoming releases.
          </p>
          
          <div className="mb-6">
            <input
              type="email"
              placeholder="Your email address"
              className="w-full px-4 py-3 bg-background-light border border-gray-700 rounded-md focus:ring-2 focus:ring-accent/50 text-white"
            />
          </div>
          
          <button 
            className="w-full px-6 py-3 bg-accent text-white font-medium rounded-md hover:bg-accent/90 transition-colors"
          >
            Subscribe
          </button>
          
          <p className="text-xs text-gray-400 text-center mt-4">
            We respect your privacy. Unsubscribe at any time.
          </p>
        </div>
        
        {/* Contact Title */}
        <div className="text-center">
          <h3 className="text-lg text-gray-300">
            Get in touch with our team
          </h3>
        </div>
        
        {/* Contact Form */}
        <div id="contact-form" className="bg-[#0c0c0c] rounded-lg p-5 w-full max-w-xl mx-auto">
          {/* Status Message */}
          {submitStatus.type && (
            <div 
              className={`mb-4 p-3 rounded-lg flex items-center gap-2 ${
                submitStatus.type === 'success' 
                  ? 'bg-green-900/20 text-green-400 border border-green-800' 
                  : 'bg-red-900/20 text-red-400 border border-red-800'
              }`}
            >
              {submitStatus.type === 'success' ? <FiCheck /> : <FiAlertCircle />}
              <span className="text-sm">{submitStatus.message}</span>
            </div>
          )}
          
          <form onSubmit={handleSubmit} className="space-y-3">
            <div className="flex items-center justify-center mb-3">
              <FiMessageSquare className="text-accent mr-2" />
              <span className="text-white font-medium">Send Us a Message</span>
            </div>
            
            <div>
              <label htmlFor="name" className="block text-sm text-gray-400 mb-1">
                Your Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                className="w-full px-4 py-2 bg-[#161616] border border-gray-700 rounded-md text-white"
                required
              />
            </div>
            
            <div>
              <label htmlFor="email" className="block text-sm text-gray-400 mb-1">
                Your Email <span className="text-red-500">*</span>
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                className="w-full px-4 py-2 bg-[#161616] border border-gray-700 rounded-md text-white"
                required
              />
            </div>
            
            <div>
              <label htmlFor="subject" className="block text-sm text-gray-400 mb-1">
                Subject
              </label>
              <input
                type="text"
                id="subject"
                name="subject"
                value={formData.subject}
                onChange={handleChange}
                className="w-full px-4 py-2 bg-[#161616] border border-gray-700 rounded-md text-white"
                placeholder="What's this regarding?"
              />
            </div>
            
            <div>
              <label htmlFor="message" className="block text-sm text-gray-400 mb-1">
                Message <span className="text-red-500">*</span>
              </label>
              <textarea
                id="message"
                name="message"
                value={formData.message}
                onChange={handleChange}
                rows={4}
                className="w-full px-4 py-2 bg-[#161616] border border-gray-700 rounded-md text-white resize-none"
                placeholder="How can we help you?"
                required
              ></textarea>
            </div>
            
            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full flex items-center justify-center px-4 py-2 bg-accent text-white font-medium rounded-md hover:bg-accent/90 transition-colors"
            >
              {isSubmitting ? (
                <span className="flex items-center">
                  <svg className="animate-spin mr-2 h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Sending...
                </span>
              ) : (
                <span className="flex items-center">
                  <FiSend className="mr-2" />
                  Send Message
                </span>
              )}
            </button>
          </form>
        </div>
        
        {/* Reach Out Section */}
        <div className="bg-[#0c0c0c] rounded-lg p-5 w-full max-w-xl mx-auto">
          <h3 className="text-white font-medium mb-3 text-center">Reach Out</h3>
          <p className="text-gray-400 text-sm mb-4 text-center">
            Have questions about our products, shipping, or returns? 
            We're here to help with any inquiries you might have.
          </p>
          
          <div className="space-y-4 max-w-md mx-auto">
            <a 
              href="mailto:contact-us@grailed804.com" 
              className="flex items-center group"
            >
              <div className="h-10 w-10 rounded-full bg-accent/10 flex items-center justify-center mr-3">
                <FiMail className="text-accent" />
              </div>
              <div>
                <div className="text-xs text-gray-500">Email Us</div>
                <span className="text-white text-sm">contact-us@grailed804.com</span>
              </div>
            </a>
            
            <a 
              href="https://www.instagram.com/grailed804"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center group"
            >
              <div className="h-10 w-10 rounded-full bg-accent/10 flex items-center justify-center mr-3">
                <FiInstagram className="text-accent" />
              </div>
              <div>
                <div className="text-xs text-gray-500">Instagram</div>
                <span className="text-white text-sm">@grailed804</span>
              </div>
            </a>
            
            <a 
              href="https://www.whatnot.com/user/grailed804"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center group"
            >
              <div className="h-10 w-10 rounded-full bg-accent/10 flex items-center justify-center mr-3">
                <RiWhatsappFill className="text-accent" />
              </div>
              <div>
                <div className="text-xs text-gray-500">Whatnot</div>
                <span className="text-white text-sm">@grailed804</span>
              </div>
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactUs; 